import React, { useEffect, useState } from "react";

import { useEditorState } from "@/store";
import { localizeCircleFlowText } from "@/utils";

type AchievementProps = {
  achievement_id: number | string;
};

const TrophyIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 3h12v7a6 6 0 0 1-12 0V3z" />
    <path d="M6 5H3.5a1.5 1.5 0 0 0-1.5 1.5v1a3.5 3.5 0 0 0 3.5 3.5H6" />
    <path d="M18 5h2.5a1.5 1.5 0 0 1 1.5 1.5v1a3.5 3.5 0 0 1-3.5 3.5H18" />
    <path d="M12 16v3" />
    <path d="M8 20h8" />
  </svg>
);

const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export const Achievement: React.FC<AchievementProps> = ({
  achievement_id,
}) => {
  const { achievements, circleFlow } = useEditorState();

  const [achievement, setAchievement] = useState(
    achievements?.find((ach) => ach.id === achievement_id)
  );

  useEffect(() => {
    setAchievement(achievements?.find((ach) => ach.id === achievement_id));
  }, [achievements]);

  const isAchieved = achievement?.is_achieved ?? false;

  // Same "current active sub circle" lookup as CircleFlowBar's `selectedEntry`.
  const selectedEntry =
    circleFlow
      ?.flatMap((group) => group.subcircles.map((sub) => ({ sub, group })))
      .find(({ sub }) => sub.selected) ?? null;

  const moduleIndex = selectedEntry
    ? `${selectedEntry.group.group_index}.${selectedEntry.sub.stage}`
    : "";
  const title = localizeCircleFlowText(selectedEntry?.sub.title);

  return (
    <div className="w-full max-w-[650px] bg-white rounded-[22px] overflow-hidden font-alata select-none pl-2 pt-2">
      <div className="flex flex-col space-y-4">
        {/* Step Badge & Header Title */}
        <div className="flex items-center gap-2 px-2 ">
          <div className="w-[28px] h-[28px] xl:w-[36px] xl:h-[36px] 2xl:w-[43px] 2xl:h-[43px] flex items-center justify-center shrink-0">
            <div
              className={`w-[22px] h-[22px] xl:w-[29px] xl:h-[29px] 2xl:w-[34px] 2xl:h-[34px] rounded-[5px] rotate-45 flex items-center justify-center shrink-0 shadow-xs transition-colors duration-250 ${isAchieved ? "bg-[#05ca75]" : "bg-[#0096cb]"
                }`}
            >
              <span className="-rotate-45 text-white font-bold text-[11px] xl:text-[15px] 2xl:text-[17px] leading-none tracking-tight select-none text-center px-1">
                {moduleIndex}
              </span>
            </div>
          </div>

          <h2 className="text-[11px] xl:text-[15px] 2xl:text-[17px] font-[500] text-[#141b34] tracking-tight leading-snug font-alata pl-2 ">
            {title}
          </h2>
        </div>

        {/* Description */}
        <p className="text-[10px] xl:text-[12px] 2xl:text-[14px] leading-[1.65] text-[#141b34] font-normal px-2">
          {achievement?.description}
        </p>

        {/* Checkpoint Status Banner */}
        <div className="pt-1 pl-2">
          {isAchieved ? (
            <div className="w-fit max-w-full bg-[#05ca75] rounded-full p-1.5 pr-8 flex items-center gap-4.5 shadow-xs transition-all">
              <div className="w-[37px] h-[37px] xl:w-[48px] xl:h-[48px] 2xl:w-[57px] 2xl:h-[57px] rounded-full bg-[#01ff47] flex items-center justify-center shrink-0">
                <div className="w-[30px] h-[30px] xl:w-[39px] xl:h-[39px] 2xl:w-[46px] 2xl:h-[46px] rounded-full bg-[#05ca75] flex items-center justify-center">
                  <div className="w-[21px] h-[21px] xl:w-[27px] xl:h-[27px] 2xl:w-[32px] 2xl:h-[32px] rounded-full border-[2.4px] border-white flex items-center justify-center">
                    <CheckIcon className="w-[13px] h-[13px] xl:w-[16px] xl:h-[16px] 2xl:w-[19px] 2xl:h-[19px] text-white" />
                  </div>
                </div>
              </div>
              <span className="text-white text-[10.5px] xl:text-[13.5px] 2xl:text-[15.5px] font-semibold tracking-normal pr-2">
                {achievement?.name}
              </span>
            </div>

          ) : (
            <div className="w-fit max-w-full bg-[#e6f7fc] rounded-full p-1.5 pr-8 flex items-center gap-4.5 shadow-xs transition-all">
              <div className="w-[37px] h-[37px] xl:w-[48px] xl:h-[48px] 2xl:w-[57px] 2xl:h-[57px] rounded-full bg-[#cbd3da] flex items-center justify-center shrink-0">
                <div className="w-[30px] h-[30px] xl:w-[39px] xl:h-[39px] 2xl:w-[46px] 2xl:h-[46px] rounded-full bg-[#0096cb] flex items-center justify-center shadow-xs">
                  <TrophyIcon className="w-[14px] h-[14px] xl:w-[18px] xl:h-[18px] 2xl:w-[22px] 2xl:h-[22px] text-white" />
                </div>
              </div>
              <span className="text-[#141b34] text-[10px] xl:text-[12px] 2xl:text-[14px] font-normal tracking-normal pr-2">
                {achievement?.name}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
